import { ComponentProps, useMemo, useState } from "react";
import { CalendarIcon } from "lucide-react";
import type { Matcher, DateBefore, DateAfter } from "react-day-picker";
import { fromZonedTime } from "date-fns-tz";

import { Button, ButtonProps } from "./button";
import { Calendar } from "./calendar";
import { Input } from "./input";
import { Label } from "./label";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { cn, DateFormat, DateTimeFormat, formatDateTime, formatTime, parseDateInput } from "@tellescope/utils";
import { useDateFnsFormattersWithLocale, useStateWithSetAction } from "../hooks";
import { useTranslation } from "react-i18next";

export interface DatePickerProps {
  label?: string;
  placeholder?: string;
  value?: Date;
  onChange?: (date: Date | undefined) => void;
  onBlur?: () => void;
  onClose?: () => void;
  disabledDates?: Matcher | Matcher[]; // Dates to disable in calendar
  disabled?: boolean; // Whether the entire input is disabled
  className?: string;
  id?: string;
  displayFormat?: DateFormat;
  buttonProps?: Omit<ButtonProps, "disabled">;
  inputProps?: Partial<Pick<ComponentProps<typeof Input>, "className">>;
}

export interface DateTimePickerProps<HideTime extends boolean = false> extends Omit<DatePickerProps, "displayFormat"> {
  hideTime?: HideTime;
  showSeconds?: boolean;
  displayFormat?: HideTime extends true ? DateFormat : DateTimeFormat;
  timezone?: string;
}

export const DateTimePicker = <HideTime extends boolean = false>({
  label = "Date",
  placeholder,
  value,
  onChange,
  onBlur,
  onClose,
  disabledDates,
  disabled = false,
  className = "",
  id = "date-time-picker",
  hideTime,
  showSeconds = false,
  displayFormat,
  timezone,
  buttonProps: { className: buttonClassName, ...buttonProps } = {},
  inputProps: { className: inputClassName, ...inputProps } = {},
}: DateTimePickerProps<HideTime>) => {
  const { formatInTimeZone } = useDateFnsFormattersWithLocale();
  const { t } = useTranslation("ui");
  const [open, setOpen] = useStateWithSetAction(false, (isOpen) => {
    if (!isOpen) {
      onClose?.();
    }
  });
  const [displayMonth, setDisplayMonth] = useState(value);
  const [inputValue, setInputValue] = useState<string | null>(null);
  const [isFocused, setIsFocused] = useState(false);

  const timeValue = useMemo(() => {
    if (!value || hideTime) return undefined;
    if (timezone) return formatInTimeZone(value, timezone, showSeconds ? "HH:mm:ss" : "HH:mm");
    return formatTime(value, showSeconds ? "HH:mm:ss" : "HH:mm");
  }, [value, hideTime, timezone, formatInTimeZone, showSeconds]);

  // Detect if disabledDates matcher includes "before: Date" to infer requireFuture behavior
  const requireFuture = useMemo(() => {
    if (!disabledDates) return false;

    // Check if disabledDates is an object with "before" property
    if (typeof disabledDates === "object" && !Array.isArray(disabledDates) && "before" in disabledDates) {
      return true;
    }

    // Check if disabledDates is an array containing an object with "before" property
    if (Array.isArray(disabledDates)) {
      return disabledDates.some(
        (matcher) => typeof matcher === "object" && !Array.isArray(matcher) && "before" in matcher
      );
    }

    return false;
  }, [disabledDates]);

  // Infer fromDate for month navigation from disabledDates matcher
  const inferredFromDate = useMemo(() => {
    if (!disabledDates) return undefined;

    // Check if disabledDates is DateBefore object: { before: Date }
    if (typeof disabledDates === "object" && !Array.isArray(disabledDates) && "before" in disabledDates) {
      return disabledDates.before;
    }

    // Check if disabledDates is an array containing DateBefore
    if (Array.isArray(disabledDates)) {
      const beforeMatcher = disabledDates.find(
        (matcher) => typeof matcher === "object" && !Array.isArray(matcher) && "before" in matcher
      ) as DateBefore | undefined;
      return beforeMatcher?.before;
    }

    return undefined;
  }, [disabledDates]);

  // Infer toDate for month navigation from disabledDates matcher
  const inferredToDate = useMemo(() => {
    if (!disabledDates) return undefined;

    // Check if disabledDates is DateAfter object: { after: Date }
    if (typeof disabledDates === "object" && !Array.isArray(disabledDates) && "after" in disabledDates) {
      return disabledDates.after;
    }

    // Check if disabledDates is an array containing DateAfter
    if (Array.isArray(disabledDates)) {
      const afterMatcher = disabledDates.find(
        (matcher) => typeof matcher === "object" && !Array.isArray(matcher) && "after" in matcher
      ) as DateAfter | undefined;
      return afterMatcher?.after;
    }

    return undefined;
  }, [disabledDates]);

  // Calculate minimum time if requireFuture is true and selected date is today
  const minTime = useMemo(() => {
    if (!requireFuture || hideTime || !value) return undefined;

    const now = new Date();
    const selectedDate = new Date(value);

    // Check if selected date is today
    const isToday =
      selectedDate.getFullYear() === now.getFullYear() &&
      selectedDate.getMonth() === now.getMonth() &&
      selectedDate.getDate() === now.getDate();

    if (isToday) {
      return formatTime(now, showSeconds ? "HH:mm:ss" : "HH:mm");
    }

    return undefined;
  }, [requireFuture, hideTime, value, showSeconds]);

  const formattedValue = useMemo(() => {
    if (!value) return "";

    if (timezone && !hideTime) {
      return formatInTimeZone(
        value,
        timezone,
        showSeconds ? "MMM d, yyyy 'at' hh:mm:ss a zzz" : "MMM d, yyyy 'at' hh:mm a zzz"
      );
    }
    if (hideTime) {
      return formatInTimeZone(value, timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone, "MMMM d, yyyy");
    } else if (showSeconds) {
      return formatDateTime(value, displayFormat || "MMM D, YYYY at hh:mm:ss a");
    }
    return formatDateTime(value, displayFormat || "MMM D, YYYY at hh:mm a");
  }, [value, timezone, hideTime, showSeconds, displayFormat, formatInTimeZone]);

  // When focused, show the user's typed input; otherwise show formatted value
  const displayValue = isFocused && inputValue !== null ? inputValue : formattedValue;

  const handleDateSelect = (selectedDate: Date | undefined) => {
    if (selectedDate && timezone && !hideTime) {
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
      const day = String(selectedDate.getDate()).padStart(2, "0");
      const time = timeValue ?? formatInTimeZone(new Date(), timezone, "HH:mm");
      onChange?.(fromZonedTime(`${year}-${month}-${day}T${time}`, timezone));
      return;
    }
    if (selectedDate && !hideTime && timeValue) {
      // Preserve time when selecting date
      const timeParts = timeValue.split(":").map(Number);
      const hours = timeParts[0] ?? 0;
      const minutes = timeParts[1] ?? 0;
      const seconds = timeParts[2] ?? 0;
      selectedDate.setHours(hours, minutes, showSeconds ? seconds : 0, 0);
    } else if (selectedDate && !timeValue) {
      // Default to current time if no previous time (or next hour if requireFuture)
      const currentTime = new Date();
      if (requireFuture) {
        // Set to next hour to ensure it's in the future
        currentTime.setHours(currentTime.getHours() + 1, 0, 0, 0);
      }
      const hours = currentTime.getHours();
      const minutes = currentTime.getMinutes();
      const seconds = showSeconds ? currentTime.getSeconds() : 0;
      selectedDate.setHours(hours, minutes, seconds, 0);
    }
    if (hideTime) {
      setOpen(false);
    }
    onChange?.(selectedDate);
  };

  const handlePopoverClose = (newOpen: boolean) => {
    if (!newOpen) {
      setTimeout(() => {
        // Timeout to avoid state change before finishing closing
        setDisplayMonth(undefined);
      });
    }

    setOpen(newOpen);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    // Try to parse the input as a date
    const parsedDate = parseDateInput(newValue);
    if (parsedDate) {
      // If hideTime is true and user entered a date without time, use the parsed date directly
      // If we have an existing time and the parsed date doesn't have a specific time set by user,
      // preserve the existing time
      if (hideTime) {
        // For date-only picker, just use midnight
        parsedDate.setHours(0, 0, 0, 0);
        onChange?.(parsedDate);
      } else {
        // For datetime picker, the parseDateInput already handles time parsing
        // If no time was in the input, parseDateInput defaults to midnight
        // We should preserve existing time if user only typed a date
        const hasTimeInInput = /\d{1,2}:\d{2}/.test(newValue);
        if (!hasTimeInInput && timeValue) {
          // User typed date only - preserve existing time
          const timeParts = timeValue.split(":").map(Number);
          const hours = timeParts[0] ?? 0;
          const minutes = timeParts[1] ?? 0;
          const seconds = timeParts[2] ?? 0;
          parsedDate.setHours(hours, minutes, showSeconds ? seconds : 0, 0);
        }
        onChange?.(parsedDate);
      }
    }
    // If parsing fails, we just keep the input value displayed
    // so the user can continue typing
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = e.target.value;

    if (newTime) {
      if (timezone) {
        const currentDateStr = formatInTimeZone(value ?? new Date(), timezone, "yyyy-MM-dd");
        const newDate = fromZonedTime(`${currentDateStr}T${newTime}`, timezone);
        if (requireFuture && newDate.getTime() <= Date.now()) return;
        onChange?.(newDate);
        return;
      }

      const timeParts = newTime.split(":").map(Number);
      const hours = timeParts[0] ?? 0;
      const minutes = timeParts[1] ?? 0;
      const seconds = timeParts[2] ?? 0;
      const newDate = value ? new Date(value) : new Date();
      newDate.setHours(hours, minutes, showSeconds ? seconds : 0, 0);

      // If requireFuture is true, don't allow past times
      if (requireFuture && newDate.getTime() <= Date.now()) {
        return;
      }

      onChange?.(newDate);
    }
  };

  const handleInputFocus = () => {
    setIsFocused(true);
    // Initialize inputValue with the current formatted value when focusing
    setInputValue(formattedValue);
  };

  const handleInputBlur = () => {
    setIsFocused(false);
    // If the current inputValue doesn't parse to a valid date, revert to the formatted value
    if (inputValue !== null) {
      const parsedDate = parseDateInput(inputValue);
      if (!parsedDate && inputValue.trim() !== "") {
        // Invalid input - it will revert to formattedValue since isFocused is false
      } else if (inputValue.trim() === "" && value) {
        // User cleared the input - clear the value
        onChange?.(undefined);
      }
    }
    setInputValue(null);
    onBlur?.();
  };

  return (
    <div className={`flex flex-col ${className?.includes("space-y") ? "" : "gap-1.5"} ${className}`}>
      {label && (
        <Label htmlFor={id} className="px-1">
          {label}
        </Label>
      )}
      <div className="relative">
        <Input
          id={id}
          value={displayValue}
          placeholder={
            placeholder ||
            formatDateTime(
              new Date(),
              hideTime ? "MMMM D, YYYY" : showSeconds ? "MMM D, YYYY at hh:mm:ss a" : "MMM D, YYYY at hh:mm a"
            )
          }
          className={cn("bg-background pr-10", inputClassName)}
          disabled={disabled}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setOpen(true);
            }
          }}
          {...inputProps}
        />
        <Popover open={open} onOpenChange={handlePopoverClose} modal={false}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              className={cn("absolute top-1/2 right-2 size-6 -translate-y-1/2", buttonClassName)}
              disabled={disabled}
              {...buttonProps}
            >
              <CalendarIcon className="size-3.5" />
              <span className="sr-only">{t("Select date")}</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-auto overflow-hidden p-0"
            align="end"
            alignOffset={-8}
            sideOffset={10}
            forceMount
            style={{
              zIndex: 9999,
              visibility: "visible",
              display: "block",
            }}
          >
            <Calendar
              mode="single"
              selected={value}
              captionLayout="dropdown"
              month={displayMonth ?? value}
              onMonthChange={setDisplayMonth}
              onSelect={handleDateSelect}
              disabled={disabledDates}
              hidden={
                inferredFromDate && inferredToDate
                  ? {
                      before: inferredFromDate,
                      after: inferredToDate,
                    }
                  : inferredFromDate
                  ? {
                      before: inferredFromDate,
                    }
                  : inferredToDate
                  ? {
                      after: inferredToDate,
                    }
                  : undefined
              }
            />
            {!hideTime && (
              <div className="border-t p-3">
                <Label className="text-sm font-medium mb-2 block">{t("Time")}</Label>
                <Input
                  type="time"
                  value={timeValue}
                  onChange={handleTimeChange}
                  disabled={disabled}
                  className="bg-background w-full"
                  step={showSeconds ? 1 : 60}
                  min={minTime}
                />
              </div>
            )}
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};

export const DatePicker = ({ ...args }: DatePickerProps) => {
  return DateTimePicker<true>({
    id: "date-picker",
    ...args,
    hideTime: true,
  });
};
